import { Box, Tooltip as MUITooltip } from '@mui/material';
import { getPlanningKpisRequest } from 'api/requests/kpis-requests';
import { UPInlineDropdown } from 'components/dropdown';
import { PlanningKpiType } from 'constants/PlanningKpiType';
import ContractsGraph from 'modules/dashboard/tiles/contracts/ContractsGraph';
import { buildContractsGraphData } from 'modules/dashboard/tiles/contracts/contractsGraphData';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DASHBOARD__KPI_FETCH_ERROR,
  DASHBOARD__PLANNING_KPIS,
  DASHBOARD__PLANNING_KPIS_CONTRACTS_CREATED,
  DASHBOARD__PLANNING_KPIS_PLANNED_HOURS,
} from 'translations/constants';
import { IPlanningKpiBucket } from 'types/kpis/IPlanningKpiBucket';
import { ReactComponent as WarningIcon } from '../../../../assets/icons/ContractErrorIcon.svg';
import { getErrorFallbackPoints, toGraphPoints } from './planningKpisData';
import { PlanningKpisGraphContainer, PlanningKpisHeader, PlanningKpisSection, PlanningKpisTitle } from './styles';

const DashboardPlanningKpisTile = (): JSX.Element => {
  const [t] = useTranslation();

  const [kpiType, setKpiType] = useState<string>(String(PlanningKpiType.PLANNED_HOURS));
  const [buckets, setBuckets] = useState<IPlanningKpiBucket[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setHasError(false);
    getPlanningKpisRequest({ kpiType: Number(kpiType) as PlanningKpiType })
      .then(data => {
        if (!cancelled) setBuckets(data);
      })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
        setBuckets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [kpiType]);

  const dropdownItems = useMemo(
    () => [
      { value: String(PlanningKpiType.PLANNED_HOURS), label: t(DASHBOARD__PLANNING_KPIS_PLANNED_HOURS) },
      { value: String(PlanningKpiType.CONTRACTS_CREATED), label: t(DASHBOARD__PLANNING_KPIS_CONTRACTS_CREATED) },
    ],
    [t],
  );

  const points = useMemo(() => {
    if (hasError) return getErrorFallbackPoints(t);
    return toGraphPoints(buckets, t);
  }, [buckets, hasError, t]);

  const { data, maxValue, ticks } = useMemo(() => buildContractsGraphData(points, { paddingEnds: 'narrow' }), [points]);

  return (
    <PlanningKpisSection>
      <PlanningKpisHeader>
        <Box display="flex" alignItems="center" gap="6px">
          <PlanningKpisTitle>{t(DASHBOARD__PLANNING_KPIS)}</PlanningKpisTitle>
          {hasError && (
            <MUITooltip title={t(DASHBOARD__KPI_FETCH_ERROR)} placement="right" arrow>
              <Box display="flex" alignItems="center">
                <WarningIcon width={20} height={20} />
              </Box>
            </MUITooltip>
          )}
        </Box>
        <UPInlineDropdown items={dropdownItems} value={kpiType} onChange={setKpiType} id="planning-kpis-type" />
      </PlanningKpisHeader>
      <PlanningKpisGraphContainer>
        {data.length > 0 && (
          <ContractsGraph
            data={data}
            maxValue={maxValue}
            ticks={ticks}
            valueFontSize={16}
            weekFontSize={12}
            weekColor="#9EA2A2"
            areaFill="#71B790"
            lineStrokeWidth={3}
            dotRadius={6}
            dotStrokeWidth={2}
            weekTickMargin={6}
          />
        )}
      </PlanningKpisGraphContainer>
    </PlanningKpisSection>
  );
};

export default DashboardPlanningKpisTile;
